/**
 * Byte accounting for the size ladder (docs/ARCHITECTURE.md §1.5).
 *
 * `new Blob([text]).size` would be the obvious answer and it is wrong here twice: `Blob`
 * does not exist in `lib: ["ES2022"]` (gate 1), and it allocates a copy of the document on
 * every keystroke. The soft/hard limits are consulted on every edit of a document that may
 * be 10 MiB, so this walks the string instead.
 */

const KIB = 1024;

/** Units are binary (KiB, not kB): the thresholds in `src/app/config.ts` are powers of two. */
const UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const;

/**
 * Length of `text` when encoded as UTF-8, in bytes.
 *
 * Matches `new TextEncoder().encode(text).length` exactly, including the WHATWG rule that
 * an unpaired surrogate is encoded as U+FFFD (three bytes).
 */
export function utf8ByteLength(text: string): number {
  let bytes = 0;

  for (let i = 0; i < text.length; i += 1) {
    const unit = text.charCodeAt(i);

    if (unit < 0x80) {
      bytes += 1;
    } else if (unit < 0x800) {
      bytes += 2;
    } else if (unit >= 0xd800 && unit <= 0xdbff) {
      // High surrogate: four bytes when it is followed by its low half, otherwise it is a
      // lone surrogate and the encoder substitutes U+FFFD.
      const next = i + 1 < text.length ? text.charCodeAt(i + 1) : 0;
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else {
        bytes += 3;
      }
    } else {
      // Includes lone low surrogates, which also become U+FFFD (three bytes).
      bytes += 3;
    }
  }

  return bytes;
}

/**
 * Human-readable byte count for banners and the status bar: `formatBytes(1536) === '1.5 KiB'`.
 *
 * Deliberately locale-independent. This string is produced by the engine, which does not
 * speak any language (ADR-0008); a locale-aware rendering belongs in the UI if it is ever
 * wanted. Values below 1 KiB are integers; above it, one decimal with a trailing `.0`
 * trimmed.
 *
 * @throws RangeError when `bytes` is negative or not finite.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    throw new RangeError(`formatBytes: expected a non-negative finite number, got ${bytes}`);
  }

  if (bytes < KIB) {
    return `${Math.round(bytes)} B`;
  }

  let value = bytes;
  let unit = 0;
  while (value >= KIB && unit < UNITS.length - 1) {
    value /= KIB;
    unit += 1;
  }

  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} ${UNITS[unit] ?? 'B'}`;
}
